import { getStoryblokApi, StoryblokComponent, useStoryblokState } from '@storyblok/react';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import Layout from '~/components/Layout';
import Seo from '~/components/Seo';
import getCachedStates from '~/utils/fetch-cached-states';
import useStore from '~/hooks/useStore';
import getCachedStatesWithRulesData from '~/utils/fetch-states-with-rules-data';

export default ({ story: initialStory, preview }) => {
  const story = useStoryblokState(initialStory, {}, preview);
  const setPage = useStore(({ setPage }) => setPage);
  const router = useRouter();
  // get the background color of the first section and if is dark, show the light header
  const headerDark =
    Array.isArray(story?.content?.body) && story.content.body[0] && (story.content.body[0].background === 'dark-blue' || story.content.body[0].background === 'black-2');
  const [loc, setLoc] = useState({});

  useEffect(() => {
    setPage(initialStory);

    const params = router.query;
    // Geolocation localised based on ?loc=<id> — google geotargets id spec
    if (params.loc) {
      if (!loc?.name && !loc?.notFound) {
        // Fetch the data, this hook will run again as the "loc" variable is in the list of deps for this effect.
        fetch(`/api/id-to-location?id=${params.loc}`)
          .then(r => r.json())
          .then(d => setLoc(d));
        return false;
      }

      if (loc?.name) {
        [...document.querySelectorAll(`[data-replace="city"]`), ...document.querySelectorAll(`[data-replace="state"]`)].forEach(el => {
          const prepend = el.getAttribute('data-prepend') || '';
          el.innerText = prepend + loc.name;
        });
      }
    }

    // run through params on url, if for example, ?city=test&state=Utah,
    // check if any elements on the page contain data-replace="city" or data-replace="state" attrs (not limited to these attrs)
    // and replace the html content inside with the associated value
    const hasMoreThanSlugParam = Object.keys(params).length > 1;
    if (hasMoreThanSlugParam) {
      Object.keys(params)
        .filter(key => key !== 'slug')
        .forEach(key => {
          const value = params[key];
          if (value) {
            [...document.querySelectorAll(`[data-replace="${key}"]`)].forEach(el => {
              el.innerText = value;
            });
          }
        });
    }
  }, [initialStory, router, loc]);

  return (
    <Layout
      className={``}
      hideCTA={story?.content?.hideFooterCta || false}
      headerDark={headerDark}
      isLandingPage={story.content.navType === 'landing'}
      footerType={story.content.footerType}
      navType={story.content.navType}
      customHeaderButton={story.content.navButton}>
      <Seo
        title={story?.content?.seo?.title || story.name || ''}
        description={story?.content?.seo?.description || ''}
        openGraphImage={{ url: story?.content?.seoOgImage?.filename || '' }}
        robotsNoIndex={story?.content?.seoRobotsNoIndex || false}
        canonical={story?.content?.seoCanonical}
        customJsonSchema={story?.content?.seoCustomJsonSchema}
      />
      <StoryblokComponent blok={story.content} />
    </Layout>
  );
};

export const getStaticProps = async ({ params, preview = false }) => {
  try {
    const storyblokApi = getStoryblokApi();

    const sbParams = {
      version: 'published',
      resolve_relations: [
        'post.category',
        'post.relatedPosts',
        'Post.relatedPosts',
        'post.author',
        'blogList.posts',
        'careersLandingPage.testimonial',
        'careersLandingPage.testimonial2'
      ]
    };

    if (preview || process.env.NEXT_PUBLIC_VERCEL_ENV !== 'production') {
      // load the draft version inside of the preview mode
      sbParams.version = 'draft';
      sbParams.cv = Date.now();
    }

    let v1, v2;

    try {
      v1 = await storyblokApi.get(`cdn/stories/${params.slug.join('/')}`, sbParams);
    } catch (error) {
      v2 = await storyblokApi.get(`cdn/stories/v2/${params.slug.join('/')}`, sbParams);
    }

    const data = v1?.data ? v1?.data : v2?.data;

    let pricingData = [];

    try {
      if (params?.slug?.join('/')?.includes('pricing')) {
        pricingData = await getCachedStates();
        if (data && data.story && data.story.content && data?.story?.content?.body?.length > 0) {
          data.story.content.body.forEach(rs => {
            if (rs.component === 'landingPage' && rs.landingPageBlock[0].component === 'pricingLandingPage') {
              rs.landingPageBlock[0].pricingData = pricingData;
            }
          });
        }
      }

      if (params?.slug?.join('/')?.includes('service-of-process-rules')) {
        const statesWithRulesData = await getCachedStatesWithRulesData();
        data.story.statesWithRulesData = statesWithRulesData;
      }
    } catch (error) {
      console.log(err);
    }

    return {
      props: {
        story: data ? data.story : false,
        preview
      },
      revalidate: 60 // In seconds
    };
  } catch (error) {
    if (params.slug[0]) {
      if (params.slug[0] === 'get-started' || params.slug[0] === 'get-started-v3-old' || params.slug[0] === 'get-started-old') {
        return {
          redirect: {
            destination: 'https://app.proofserve.com/signup'
          }
        };
      }

      // Redirect broken careers listing to the listings page
      if (params.slug[0] === 'careers') {
        return {
          redirect: {
            destination: `/careers/`,
            permanent: true
          }
        };
      }
    }

    console.log(error);
    return {
      notFound: true
    };
  }
};

export async function getStaticPaths() {
  const storyblokApi = getStoryblokApi();
  let { data } = await storyblokApi.get('cdn/links/');

  let paths = [];
  Object.keys(data.links).forEach(linkKey => {
    if (
      data.links[linkKey].is_folder ||
      data.links[linkKey].slug === 'home' ||
      data.links[linkKey].slug.startsWith('service-areas/') ||
      data.links[linkKey].slug.startsWith('careers/') ||
      data.links[linkKey].slug.startsWith('authors/')
    ) {
      return;
    }
    const slug = data.links[linkKey].slug;
    paths.push('/' + slug);
  });

  return {
    paths: process.env.VERCEL_ENV === 'production' ? paths : paths.slice(0, 4),
    fallback: 'blocking'
  };
}
